/**
 * Client-generated, lexicographically sortable IDs.
 *
 * Every syncable entity gets its ID on the device that created it, before the
 * server has ever heard of it. That is what makes the sync protocol idempotent:
 * a retried create carries the same ID and collapses into the same row instead
 * of producing a duplicate session.
 *
 * ULID layout: 48-bit millisecond timestamp + 80 bits of randomness, encoded in
 * Crockford base32. Sortable by creation time, which keeps SQLite's b-tree
 * inserts near-append-only.
 */

const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford base32, no I L O U
const ENCODING_LEN = ENCODING.length;
const TIME_LEN = 10;
const RANDOM_LEN = 16;

function encodeTime(now: number): string {
  let out = "";
  let t = now;
  for (let i = TIME_LEN - 1; i >= 0; i--) {
    const mod = t % ENCODING_LEN;
    out = ENCODING[mod]! + out;
    t = (t - mod) / ENCODING_LEN;
  }
  return out;
}

function encodeRandom(): string {
  const bytes = new Uint8Array(RANDOM_LEN);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < RANDOM_LEN; i++) out += ENCODING[bytes[i]! % ENCODING_LEN];
  return out;
}

export function ulid(now: number = Date.now()): string {
  return encodeTime(now) + encodeRandom();
}

/** Unique id for a single sync operation, used for server-side deduplication. */
export function opId(): string {
  return ulid();
}

/**
 * Session join code shown under the QR. Short enough to read aloud across a
 * table when a camera will not focus, long enough not to be guessable at the
 * rate a session stays open.
 */
const JOIN_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no I/L/O/0/1
export function joinCode(length = 6): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) out += JOIN_ALPHABET[bytes[i]! % JOIN_ALPHABET.length];
  return out;
}

/** Opaque bearer token a joined device uses to act as one participant. */
export function joinToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
